import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse, PaginationMeta } from "./apiClient";

export interface Tag {
  id: string;
  name: string;
  is_active: boolean;
}

export interface TagPayload {
  name: string;
}

export interface PaginatedTags {
  tags: Tag[];
  pagination: PaginationMeta;
}

const QUERY_KEY = ["tags"];

const fetchTags = async (page: number): Promise<PaginatedTags> => {
  const { data: response } = await apiClient.get<ApiResponse<{ tags: Tag[]; pagination: PaginationMeta }>>(
    "/v1/admin/tags",
    { params: { page } }
  );
  return response.data;
};

export const useTags = (page: number = 1) =>
  useQuery({
    queryKey: [...QUERY_KEY, page],
    queryFn: () => fetchTags(page),
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useTagsInfinite = () =>
  useInfiniteQuery({
    queryKey: [...QUERY_KEY, "infinite"],
    queryFn: ({ pageParam }) => fetchTags(pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.total_pages
        ? lastPage.pagination.page + 1
        : undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const addTag = async (body: TagPayload): Promise<Tag> => {
  const { data: response } = await apiClient.post<ApiResponse<Tag>>("/v1/admin/tags", body);
  return response.data;
};

export const useAddTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addTag,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const updateTag = async ({ id, body }: { id: string; body: TagPayload }): Promise<Tag> => {
  const { data: response } = await apiClient.put<ApiResponse<Tag>>(`/v1/admin/tags/${id}`, body);
  return response.data;
};

export const useUpdateTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTag,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const deleteTag = async (id: string): Promise<void> => {
  await apiClient.delete(`/v1/admin/tags/${id}`);
};

export const useDeleteTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTag,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};
